import React, { useState, useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import { motion } from 'framer-motion'
import { v4 as uuidv4 } from 'uuid'

import GameLobby from './GameLobby'
import GameEntry from '../onboarding/GameEntry'
import TutorialOverlay from '../onboarding/TutorialOverlay'
import { sessionManager } from '../../utils/sessionManager'
import { GameState } from '../../types/game.types'
import VintageLayout from '../layout/VintageLayout'

interface LobbyContainerProps {
    gameState: GameState | null
    isConnected: boolean
    isReconnecting: boolean
    error: string | null
    clearError: () => void
    joinGame: (playerName: string, theme: string, packId?: string, maxPlayers?: number, roomCode?: string, forceCreate?: boolean) => void
    startGame: (roomId: string, hostId: string) => void
    addBot: (difficulty?: 'EASY' | 'MEDIUM' | 'HARD') => void
    updatePlayerTheme: (playerId: string, theme: string, validated: boolean) => void
    leaveGame: () => void
}

const LobbyContainer: React.FC<LobbyContainerProps> = ({
    gameState,
    isConnected,
    isReconnecting,
    error,
    clearError,
    joinGame,
    startGame,
    addBot,
    updatePlayerTheme,
    leaveGame
}) => {
    // Local State
    const [inLobby, setInLobby] = useState(false)
    const [playerName, setPlayerName] = useState<string>('')
    const [roomCode, setRoomCode] = useState<string>('')
    const [roomName, setRoomName] = useState<string>('')
    const [roomId, setRoomId] = useState<string>('')
    const [isHost, setIsHost] = useState(false)
    const [maxPlayers, setMaxPlayers] = useState(4)
    const [showTutorial, setShowTutorial] = useState(false)
    // const [playerTheme, setPlayerTheme] = useState<string>('') // Managed via gameState players now mostly, but kept for entry

    // Sync with GameState
    useEffect(() => {
        if (gameState?.id) {
            setRoomId(gameState.id);
            // FIX: Only overwrite roomCode if we don't have one or if the server sends a specific one (which implies changing GameState type, so we just rely on local state if valid)
            // If roomCode is empty or looks like a UUID (long), try to set it.
            // But if we already have a nice 6-digit code locally, keep it!
            if (!roomCode || roomCode.length > 6) {
                // Try to see if gameState has a 'code' property even if not typed
                const serverCode = (gameState as any).code;
                if (serverCode) {
                    setRoomCode(serverCode);
                } else {
                    // If no code from server, and we have a long ID, we might be stuck.
                    // But usually the Host CREATED the code locally.
                    // The Joiner INPUTTED the code locally.
                    // So we should just trust local state if it exists.
                    if (!roomCode) setRoomCode(gameState.id);
                }
            }
        }
    }, [gameState]);

    // Sync maxPlayers from GameState (important for guests)
    useEffect(() => {
        if (gameState && (gameState as any).maxPlayers) {
            setMaxPlayers((gameState as any).maxPlayers);
        }
    }, [gameState]);

    // Handlers
    const handleCreateGame = (name: string, maxPlayersParam: number, packId?: string) => {
        console.log('🎮 handleCreateGame CALLED', { name, maxPlayersParam, packId })
        clearError()
        setPlayerName(name)
        setIsHost(true)
        setMaxPlayers(maxPlayersParam)

        const newRoomCode = Math.floor(100000 + Math.random() * 900000).toString()
        const newRoomName = `Partie de ${name}`

        setRoomCode(newRoomCode)
        setRoomName(newRoomName)
        // setInLobby(true) // REMOVED: Wait for server confirmation to avoid Split-Brain (Ghost Lobby)

        joinGame(name, 'Mes Aventures', packId, maxPlayersParam, newRoomCode, true)
    }

    const handleJoinGame = (name: string, code?: string) => {
        console.log('🎮 Rejoindre partie:', name, code)
        clearError()
        setPlayerName(name)
        setIsHost(false)

        if (code) {
            setRoomCode(code)
            setRoomName(`Partie ${code}`)
            // FIX: Do NOT set inLobby(true) immediately. Wait for server confirmation (gameState update or socket event).
            // setInLobby(true) 
            // TRIGGER SERVER JOIN
            joinGame(name, 'Mes Aventures', undefined, undefined, code, false)
        }
    }

    const handleStartGameFromLobby = (theme: string) => {
        console.log('🎮 Démarrage de la partie depuis le lobby avec thème:', theme)
        // setPlayerTheme(theme)

        if (!roomId && !gameState?.id) {
            console.error('❌ Pas de roomId disponible, impossible de démarrer')
            return
        }

        const hostId = sessionManager.getPlayerId()
        startGame(roomId || gameState?.id || '', hostId)
    }

    const handleAddBotToLobby = (difficulty: 'EASY' | 'MEDIUM' | 'HARD' = 'EASY') => {
        console.log('🤖 Ajout d\'un adversaire numérique:', difficulty)
        addBot(difficulty)
    }

    const handleLeaveRoom = () => {
        console.log('🚺 Quitter la room')
        leaveGame(); // CALL NEW FUNCTION to clear session
        setInLobby(false)
        setRoomCode('')
        setRoomName('')
        setRoomId('')
        setIsHost(false)
        setMaxPlayers(4)
        setPlayerName('')
    }

    const handleShowTutorial = () => setShowTutorial(true)
    const handleTutorialClose = () => setShowTutorial(false)
    const handleTutorialComplete = () => setShowTutorial(false)

    // Error Handling via Toast
    useEffect(() => {
        if (error) {
            import('react-hot-toast').then(({ toast }) => {
                toast.error(error, {
                    style: {
                        background: '#330505',
                        color: '#ffaaaa',
                        border: '1px solid #ff4444',
                        fontFamily: 'Courier New, monospace'
                    }
                });
            });
            clearError();
        }
    }, [error, clearError]);

    // Logic: Show Lobby if explicitly in lobby OR if gameState says we are in LOBBY (and we have player info)
    const showGameLobby = inLobby || (gameState?.status === 'LOBBY');

    if (showGameLobby) {
        // Transformer gameState.players (Map) en Array pour GameLobby
        const playersList = gameState && gameState.players
            ? Object.values(gameState.players).map((p: any) => ({
                ...p,
                isHost: false // Will be corrected below
            }))
            : [{
                id: 'local-player',
                name: playerName,
                color: '#d4af37',
                isHost: isHost,
                isConnected: true,
                joinedAt: Date.now(),
                theme: '',
                themeValidated: false
            }];

        const currentPlayer = gameState && gameState.players
            ? Object.values(gameState.players).find(p => p.name === playerName) || playersList[0]
            : playersList[0];

        // Correction isHost
        if (isHost) {
            const local = playersList.find(p => p.name === playerName);
            if (local) local.isHost = true;
            if (currentPlayer && currentPlayer.name === playerName) (currentPlayer as any).isHost = true;
        }

        return (
            <VintageLayout>
                <GameLobby
                    roomCode={roomCode || '...'}
                    roomName={roomName || 'Chargement...'}
                    players={playersList}
                    currentPlayer={currentPlayer}
                    maxPlayers={maxPlayers}
                    isHost={isHost}
                    onStartGame={handleStartGameFromLobby}
                    onLeaveRoom={handleLeaveRoom}
                    onAddBot={handleAddBotToLobby}
                    onThemeUpdate={updatePlayerTheme}
                />
                <TutorialOverlay
                    isOpen={showTutorial}
                    onClose={handleTutorialClose}
                    onComplete={handleTutorialComplete}
                />
                <Toaster position="top-right" />
            </VintageLayout>
        )
    }

    return (
        <VintageLayout>
            <GameEntry
                onCreateGame={handleCreateGame}
                onJoinGame={handleJoinGame}
                onShowTutorial={handleShowTutorial}
            />

            <TutorialOverlay
                isOpen={showTutorial}
                onClose={handleTutorialClose}
                onComplete={handleTutorialComplete}
            />
            <Toaster position="top-right" />



            <div className="fixed bottom-4 right-4 z-40 space-y-2">
                {/* Status Indicators Copied from GameApp */}
                <div className={`px-4 py-2 rounded-full text-xs font-semibold shadow-lg ${isReconnecting
                    ? 'bg-yellow-500 text-white animate-pulse'
                    : isConnected
                        ? 'bg-green-500 text-white'
                        : 'bg-red-500 text-white'
                    }`}>
                    {isReconnecting
                        ? '🔄 Reconnexion en cours...'
                        : isConnected
                            ? '🟢 Connecté au serveur'
                            : '🔴 Serveur déconnecté'
                    }
                </div>
            </div>
        </VintageLayout>
    )
}

export default LobbyContainer
